import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Stack,
  Chip,
  Divider,
  Link as MuiLink,
  Button,
} from '@mui/material';
import LaunchIcon from '@mui/icons-material/Launch';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Link as RouterLink } from 'react-router-dom';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { oneDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

// Stable tokens + join codes seeded by backend/seed_demo.py. All read-only —
// anyone who opens these URLs sees the dashboards with no signup required.
const DEMO_TEACHER_TOKEN = 'demo-particle-physics-readonly-2026';
const DEMO_DASHBOARD_URL = `/teacher/live?token=${DEMO_TEACHER_TOKEN}`;
const STATS_COURSE_TOKEN = 'demo-stats-course-readonly-2026';
const STATS_COURSE_URL = `/teacher/course?token=${STATS_COURSE_TOKEN}`;
const NUMERICS_COURSE_TOKEN = 'demo-numerics-course-readonly-2026';
const NUMERICS_COURSE_URL = `/teacher/course?token=${NUMERICS_COURSE_TOKEN}`;

// Code-block wrapper styled to match the rest of the site — same dark theme
// as the install snippet on Welcome / the Guide.
const Code: React.FC<{ children: string; lang?: 'python' | 'bash' }> = ({ children, lang = 'python' }) => (
  <Box
    sx={{
      borderRadius: 1.5,
      overflow: 'hidden',
      my: 1,
      border: '1px solid',
      borderColor: 'divider',
      '& pre': {
        margin: '0 !important',
        background: '#0f172a !important',
        fontFamily: '"JetBrains Mono", Menlo, monospace !important',
        fontSize: '0.83rem !important',
        lineHeight: '1.55 !important',
      },
    }}
  >
    <SyntaxHighlighter
      language={lang}
      style={oneDark}
      customStyle={{ margin: 0, padding: '12px 16px', background: '#0f172a' }}
      codeTagProps={{ style: { fontFamily: '"JetBrains Mono", Menlo, monospace' } }}
    >
      {children.replace(/\n$/, '')}
    </SyntaxHighlighter>
  </Box>
);

// A labelled step in the transformation story. Eyebrow text + caption + the
// code block underneath. Keep the eyebrow letter-spaced and ALL-CAPS so the
// five steps read like a comic strip.
const Stage: React.FC<{ n: number; title: string; subtitle?: React.ReactNode; children: React.ReactNode }> = ({
  n, title, subtitle, children,
}) => (
  <Box sx={{ mb: 3.5 }}>
    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 0.5 }}>
      <Box
        sx={{
          width: 24, height: 24, borderRadius: '50%',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          backgroundColor: 'primary.main', color: '#fff',
          fontWeight: 600, fontSize: '0.78rem',
          fontFamily: '"JetBrains Mono", monospace',
        }}
      >
        {n}
      </Box>
      <Typography variant="subtitle1" sx={{ fontWeight: 600, letterSpacing: '-0.01em' }}>
        {title}
      </Typography>
    </Stack>
    {subtitle && (
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1, lineHeight: 1.6, ml: 4.5 }}>
        {subtitle}
      </Typography>
    )}
    <Box sx={{ ml: { xs: 0, sm: 4.5 } }}>{children}</Box>
  </Box>
);

// Renders the exact HTML that `show_hint("…")` produces in a student's
// notebook. Reproducing the inline styles byte-for-byte from
// jupyter-integration/cadence/progress.py:551-557 so visitors see exactly
// what a student sees — not a stylised mock.
const HintRender: React.FC<{ checkpointId: string; children: React.ReactNode }> = ({ checkpointId, children }) => (
  <Box
    sx={{
      borderLeft: '3px solid #b45309',
      padding: '8px 12px',
      background: '#fffbeb',
      margin: '4px 0',
      color: '#1f2937',
      borderRadius: 0.5,
      fontFamily: 'system-ui, sans-serif',
      fontSize: '0.9rem',
      lineHeight: 1.55,
    }}
  >
    <strong>
      💡 Hint for{' '}
      <Box
        component="code"
        sx={{
          color: '#1f2937',
          background: '#fef3c7',
          padding: '1px 5px',
          borderRadius: '3px',
          fontFamily: '"JetBrains Mono", Menlo, Consolas, monospace',
          fontSize: '0.85em',
        }}
      >
        {checkpointId}
      </Box>
    </strong>
    <Box sx={{ marginTop: '4px' }}>{children}</Box>
  </Box>
);

// Same idea for `show_solution("…")` — matches progress.py:600-603.
const SolutionRender: React.FC<{ checkpointId: string; expected?: string; solutionCode?: string }> = ({
  checkpointId, expected, solutionCode,
}) => {
  const codeChip = {
    background: '#ede9fe',
    color: '#1f2937',
    padding: '1px 5px',
    borderRadius: '3px',
    fontFamily: '"JetBrains Mono", Menlo, Consolas, monospace',
    fontSize: '0.85em',
  } as const;
  return (
    <Box
      sx={{
        borderLeft: '3px solid #6b21a8',
        padding: '8px 12px',
        background: '#faf5ff',
        margin: '4px 0',
        color: '#1f2937',
        borderRadius: 0.5,
        fontFamily: 'system-ui, sans-serif',
        fontSize: '0.9rem',
        lineHeight: 1.55,
      }}
    >
      <strong>
        💡 Solution for{' '}
        <Box component="code" sx={codeChip}>{checkpointId}</Box>
      </strong>
      {expected && (
        <Box sx={{ mt: '6px' }}>
          <strong style={{ color: '#1f2937' }}>Expected answer:</strong>{' '}
          <Box component="code" sx={codeChip}>{expected}</Box>
        </Box>
      )}
      {solutionCode && (
        <Box
          component="pre"
          sx={{
            background: '#1f2937 !important',
            color: '#e5e7eb !important',
            padding: '10px 12px',
            borderRadius: '4px',
            margin: '8px 0 0 0',
            fontSize: '0.82rem',
            lineHeight: 1.55,
            fontFamily: '"JetBrains Mono", Menlo, Consolas, monospace',
            overflowX: 'auto',
          }}
        >
          {solutionCode}
        </Box>
      )}
    </Box>
  );
};

// --- The five stages of the transformation -----------------------------------
//
// Plain teaching cell → +2 magics → autoregister output → student notebook →
// what students see on hint / solution reveal. Each step shows exactly one
// idea so a reader can grok the whole pipeline in 60 seconds.

const STAGE_1_VANILLA = `# Compute the mean of 0..99 — your normal teaching cell.
arr = list(range(100))
mean_value = sum(arr) / len(arr)
mean_value`;

const STAGE_2_ADD_MAGICS = `# At the top of the notebook:
%load_ext cadence

# ... your teaching cells unchanged ...

# At the bottom, after you've Run-All:
%cadence_autoregister`;

const STAGE_3_REGISTERED = `# autoregister writes this into <your_notebook>_registered.ipynb:
%cadence_register mean_value --comparator numeric --expected 49.5

# Compute the mean of 0..99 — your normal teaching cell.
arr = list(range(100))
mean_value = sum(arr) / len(arr)
mean_value`;

const STAGE_4_STUDENT = `# What ends up in <your_notebook>_registered_student.ipynb:
# Compute the mean of 0..99 — your normal teaching cell.
arr = list(range(100))
# Your code here
mean_value = ...

check("mean_value", mean_value)`;

// --- Component ---------------------------------------------------------------

const Demo: React.FC = () => {
  return (
    <Box sx={{ maxWidth: 880, mx: 'auto', pb: 8 }}>
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Typography
          variant="h3"
          component="h1"
          sx={{ fontWeight: 700, letterSpacing: '-0.02em', fontSize: { xs: '1.8rem', sm: '2.2rem' }, mb: 1 }}
        >
          See it on one exercise
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600, mx: 'auto' }}>
          One teaching cell, two extra lines, and what each side ends up with.
          The Guide explains the rules; this page shows the actual files.
        </Typography>
      </Box>

      {/* Live dashboard CTA — the strongest hook, kept up front */}
      <Card
        sx={{
          mb: 3,
          border: '1px solid',
          borderColor: 'primary.main',
          background: 'linear-gradient(135deg, rgba(37, 99, 235, 0.06), rgba(37, 99, 235, 0.02))',
        }}
      >
        <CardContent sx={{ p: { xs: 2.5, sm: 3.5 } }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems={{ sm: 'center' }}>
            <Box sx={{ flexGrow: 1 }}>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <VisibilityIcon color="primary" fontSize="small" />
                <Typography variant="overline" color="primary.main" sx={{ fontWeight: 600, letterSpacing: '0.08em' }}>
                  Live demo dashboard
                </Typography>
              </Stack>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
                See the teacher view of a real class
              </Typography>
              <Typography variant="body2" color="text.secondary">
                14 seeded students worked through a particle-physics lab — per-checkpoint
                solve rates, common wrong answers, stuck-student alerts, and the code +
                plot submission feed. Read-only, no signup.
              </Typography>
            </Box>
            <Button
              variant="contained"
              endIcon={<LaunchIcon />}
              component={RouterLink as any}
              to={DEMO_DASHBOARD_URL}
              size="large"
              sx={{ flexShrink: 0, alignSelf: { sm: 'center' } }}
            >
              Open dashboard
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 4 }}>
        <CardContent sx={{ p: { xs: 2.5, sm: 3 } }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
            Two seeded courses — the multi-notebook view
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            What a teacher's library looks like when they're running more than one class.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button variant="outlined" endIcon={<LaunchIcon />} component={RouterLink as any}
              to={STATS_COURSE_URL} sx={{ flex: 1, justifyContent: 'space-between', textTransform: 'none' }}
            >Intro to Statistics — Fall 2026</Button>
            <Button variant="outlined" endIcon={<LaunchIcon />} component={RouterLink as any}
              to={NUMERICS_COURSE_URL} sx={{ flex: 1, justifyContent: 'space-between', textTransform: 'none' }}
            >Numerical Methods — Spring 2026</Button>
          </Stack>
        </CardContent>
      </Card>

      <Divider sx={{ mb: 4 }}>
        <Typography variant="caption" color="text.secondary" sx={{ px: 1.5, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
          One exercise — five stages
        </Typography>
      </Divider>

      <Card>
        <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
          <Stage
            n={1}
            title="Your existing teaching cell"
            subtitle="Plain Python. Nothing about Cadence yet. Run it; the answer 49.5 ends up in the kernel."
          >
            <Code>{STAGE_1_VANILLA}</Code>
          </Stage>

          <Stage
            n={2}
            title="Add two magics, anywhere in the notebook"
            subtitle={
              <>
                <code>%load_ext cadence</code> at the top so the magics register;{' '}
                <code>%cadence_autoregister</code> at the bottom so it walks the notebook and
                reads the answers from your kernel.
              </>
            }
          >
            <Code>{STAGE_2_ADD_MAGICS}</Code>
          </Stage>

          <Stage
            n={3}
            title="Autoregister writes the registered notebook for you"
            subtitle={
              <>
                The original markdown + code stays exactly as you wrote it. A single{' '}
                <code>%cadence_register</code> line is inserted at the top of each detected
                exercise cell — id from the variable name, comparator inferred from the value's
                type (a float gets <code>numeric</code>; a list gets <code>set</code>; etc.).
                You run this file to register the lesson; commit it to git as the canonical record.
              </>
            }
          >
            <Code>{STAGE_3_REGISTERED}</Code>
          </Stage>

          <Stage
            n={4}
            title="And the student notebook"
            subtitle={
              <>
                The same registered notebook scaffolds a paired student file. Your reference
                solution is blanked out; the markdown explanation stays; a <code>check(...)</code>{' '}
                call replaces the answer. That's the file you hand out to students.
              </>
            }
          >
            <Code>{STAGE_4_STUDENT}</Code>
          </Stage>

          <Stage
            n={5}
            title="When a student gets stuck"
            subtitle={
              <>
                After enough wrong attempts the student can call <code>show_hint("mean_value")</code>{' '}
                or <code>show_solution("mean_value")</code>. Cadence renders this inline in their
                notebook — the boxes below are <strong>exactly</strong> what they see, not mocks:
              </>
            }
          >
            <Stack spacing={1.5}>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, ml: 1 }}>
                  After 2 wrong attempts: <code>show_hint("mean_value")</code>
                </Typography>
                <HintRender checkpointId="mean_value">
                  Remember: you want a single number, not the list. Try{' '}
                  <Box component="code" sx={{
                    fontFamily: '"JetBrains Mono", Menlo, monospace',
                    fontSize: '0.85em',
                    background: '#fef3c7',
                    padding: '1px 4px',
                    borderRadius: '3px',
                  }}>sum() / len()</Box>.
                </HintRender>
              </Box>
              <Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5, ml: 1 }}>
                  After 3 wrong attempts: <code>show_solution("mean_value")</code>
                </Typography>
                <SolutionRender
                  checkpointId="mean_value"
                  expected="49.5"
                  solutionCode={`arr = list(range(100))\nsum(arr) / len(arr)`}
                />
              </Box>
            </Stack>
          </Stage>

          <Stage
            n={6}
            title="Optional: collect their code or plots"
            subtitle={
              <>
                Register a checkpoint with <code>--allow-submissions</code> and students can ship
                their actual work — a code cell via <code>%%cadence_submit</code>, or a matplotlib
                figure via <code>submit_image(...)</code>. It lands in a feed on your dashboard, so
                you can scan 30 different approaches (or spot the five identical copy-pastes) at a
                glance.
              </>
            }
          >
            <Code>{`%%cadence_submit higgs.peak
counts, edges = np.histogram(m_gamma_gamma, bins=np.arange(100, 151))
peak = int(edges[counts.argmax()])

fig, ax = plt.subplots()
ax.bar(edges[:-1], counts, width=1.0)
submit_image("higgs.peak", fig)   # ships the plot too
check("higgs.peak", peak)`}</Code>

            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5, mb: 0.5, ml: 0.5 }}>
              What you see on the dashboard's submission feed:
            </Typography>
            {/* Mirrors SubmissionsPanel.tsx — a name + relative time header, then the
                rendered plot on a dark card and the syntax-highlighted code beneath. */}
            <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}>
              <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>Amelia R.</Typography>
                <Typography variant="caption" color="text.disabled">· 4 min ago · plot + code</Typography>
              </Stack>
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                  borderRadius: 1,
                  p: 1.5,
                  display: 'flex',
                  justifyContent: 'center',
                  mb: 1,
                }}
              >
                <Box
                  component="img"
                  src="/demo_higgs_plot.png"
                  alt="Student-submitted diphoton mass histogram"
                  sx={{ maxWidth: '100%', maxHeight: 280, borderRadius: 1, display: 'block' }}
                />
              </Box>
              <Code>{`counts, edges = np.histogram(m_gamma_gamma, bins=np.arange(100, 151))
peak = int(edges[counts.argmax()])`}</Code>
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5 }}>
              The live demo dashboard has the full feed — 4 student plots and 10 written
              reflections on the particle-physics lab.{' '}
              <MuiLink component={RouterLink as any} to={DEMO_DASHBOARD_URL} underline="hover">
                Open it and expand the discovery.higgs-peak checkpoint →
              </MuiLink>
            </Typography>
          </Stage>
        </CardContent>
      </Card>

      <Box sx={{ textAlign: 'center', mt: 4 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
          Ready to wire your own notebook?
        </Typography>
        <Stack direction="row" spacing={1.5} justifyContent="center" sx={{ flexWrap: 'wrap', gap: 1 }}>
          <Chip
            label="→ Setup guide"
            clickable
            component={RouterLink as any}
            to="/guide"
            color="primary"
          />
          <Chip
            label="Create an account"
            clickable
            component={RouterLink as any}
            to="/signup"
            variant="outlined"
          />
          <Chip
            label="What is Cadence?"
            clickable
            component={RouterLink as any}
            to="/about"
            variant="outlined"
          />
        </Stack>
        <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
          Want the full files behind the dashboard above?{' '}
          <MuiLink href="/demos/demo-teacher-setup.ipynb" download underline="hover">
            teacher.ipynb
          </MuiLink>
          {' · '}
          <MuiLink href="/demos/demo-with-cadence.ipynb" download underline="hover">
            student.ipynb
          </MuiLink>
        </Typography>
      </Box>
    </Box>
  );
};

export default Demo;
